import video_call_icon from 'src/assets/video_call_icon.png';
import close_icon_purple from 'src/assets/close_icon_purple.png';

import { Popover } from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JournalContext } from 'src/consumer/context/JournalContext'
import { LocalizationContext } from 'src/consumer/context/LocalizationContext'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { hasCompletedConsumerOnboarding } from 'src/consumer/helpers/onboardingStatus'
import {
  prefetchWellnessTile,
  useWellnessNavTilePrefetch,
} from 'src/consumer/helpers/wellnessTilePrefetch'
import {
  canEventBeJoined,
  checkIfAllowedToJoinAllDay,
  checkIfEventUpcoming,
} from '../MyLiveSessions/my-live-sessions-utils'
import {
  BrandLogoWithLocation,
  GlobalNavProfileDropdown,
  NextSessionStrip,
  SlotNotificationPage,
  WellnessPrimaryNav,
} from './GlobalNavComponents'
import {
  BookedSlotsDrawer,
  BrandStack,
  GlobalNavCenter,
  GlobalNavContainer,
  GlobalNavLeft,
  GlobalNavParent,
  GlobalNavRight,
  JournalIcon,
  OneOnOneCountForIcon,
  SessionIcon,
  TrialBox,
} from './GlobalNavStyles'
import NotificationsList from '../Notifications/NotificationsList'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import {
  buildWellnessOnboardingPath,
  buildWellnessTilePath,
} from 'src/consumer/helpers/wellnessRoutes'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { showRedirectStrip } from '../ConsumerHomepage'
import PortalSwitcher from './PortalSwitcher'

const millisecondsInDay = 60 * 60 * 24 * 1000

function getTrialExpiryDays(subscriptionEndDate, currentTime) {
  const endDate = dayjs(subscriptionEndDate)
  const now = dayjs(currentTime)

  if (!endDate.isValid() || !now.isValid()) {
    return null
  }

  return Math.ceil(endDate.diff(now, 'millisecond') / millisecondsInDay)
}

function getTrialBannerText(profileData, currentTime) {
  const isFreeUser = profileData?.subscriptionTypeId === 'subscription_free'
  const isTrialSubscription = profileData?.subscriptionPlanType === 'freeTrial'

  if (isFreeUser) {
    return profileData?.trialOffered ? 'Trial Expired' : ''
  }

  if (!isTrialSubscription) {
    return ''
  }

  const trialExpiryDays = getTrialExpiryDays(profileData?.subscriptionEndDate, currentTime)
  if (trialExpiryDays === null) {
    return ''
  }
  if (trialExpiryDays <= 0) {
    return 'Trial Expired'
  }

  return `Trial expires in ${trialExpiryDays} ${trialExpiryDays === 1 ? 'day' : 'days'}`
}

const GlobalNav = ({
  tabOrder,
  tabsListMap,
  activeTile,
  unSecureSetupData,
  isMobile,
  openPrewallFunction,
  bookedSlotsDataPageOne,
  userScoreData,
  updateUserScoreData,
}) => {
  const navigate = useNavigate()
  const { journalData, setJournalData } = useContext(JournalContext)
  const { profileData } = useContext(ProfileContext)
  const { setupData } = useContext(SetupContext)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  } // keeping treack of width
  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth)
    // Clean up the event listener on component unmount
    return () => window.removeEventListener('resize', updateWindowWidth)
  }, []) // Empty dependency array ensures the event listener is only added once

  const [anchorElBookedSlots, setAnchorElBookedSlots] = useState()
  const handleBookedSlotPopoverClosed = () => {
    setAnchorElBookedSlots(null)
  }
  const handleOpenBookedSlots = (event) => {
    setAnchorElBookedSlots(event.currentTarget)
  }
  const bookedSlotsOpen = Boolean(anchorElBookedSlots)

  const [currentTime, setCurrentTime] = useState(dayjs().format('YYYY-MM-DD HH:mm:ss'))
  const [areAllStripsVisible, setAreAllStripsVisible] = useState(false)

  const [bookedSlotsDrawerOpen, setBookedSlotsDrawerOpen] = useState(false)

  const pageId = 'tilePage'

  const { localizedDataLoading, localizedData, localizedDataError } =
    useContext(LocalizationContext)
  const localizedStrings = localizedData?.[pageId]

  const pages = useMemo(() => {
    const nextPages = []

    // ROLE_PATIENT no longer contributes a 'patient-home' entry to the wellness
    // page-switcher dropdown — patients use the dedicated /patient-home/* portal.
    if (profileData && hasCompletedConsumerOnboarding(profileData)) {
      tabOrder?.tabs?.forEach((tab) => {
        if (tabsListMap.get(tab)?.curatable) {
          nextPages.push({ value: tab, label: tabsListMap.get(tab)?.title })
        }
      })
      tabOrder?.tiles?.forEach((tile) => {
        if (tabsListMap.get(tile)?.curatable) {
          nextPages.push({ value: tile, label: tabsListMap.get(tile)?.title })
        }
      })
    } else {
      nextPages.push({ value: 'explore-wellness-app', label: 'Explore Wellness App' })
    }

    return nextPages
  }, [profileData, tabOrder, tabsListMap])
  useWellnessNavTilePrefetch(pages, activeTile)

  const activePageLabel = pages?.find((page) => page?.value === activeTile)?.label

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(dayjs().format('YYYY-MM-DD HH:mm:ss'))
    }, 30000)

    return () => clearInterval(interval)
  }, []) //setting time every minute

  const trialBanner = getTrialBannerText(profileData, currentTime)
  let meetingPlatformImage = video_call_icon

  // UPCOMING SESSIONS STRIP(s):

  const eventsNotYetEndedList = bookedSlotsDataPageOne?.filter(
    (item) => checkIfEventUpcoming(item, currentTime) || checkIfAllowedToJoinAllDay(item),
  ) // slots with end time after current time

  eventsNotYetEndedList.reverse()
  let nextAvailableSlot
  if (eventsNotYetEndedList && eventsNotYetEndedList?.length > 0) {
    nextAvailableSlot = eventsNotYetEndedList?.[0]
  }

  return (
    <GlobalNavContainer isMobile={isMobile} leavePaddingTop= {showRedirectStrip(window.location.pathname)} dark={true}>
      <GlobalNavParent>
        <GlobalNavLeft>
          <BrandStack>
            <BrandLogoWithLocation />
            {trialBanner && (
              <TrialBox onClick={openPrewallFunction} darkText={false}>
                {trialBanner}
              </TrialBox>
            )}
          </BrandStack>
        </GlobalNavLeft>

        <GlobalNavCenter>
          <WellnessPrimaryNav
            options={pages}
            activeValue={activeTile}
            isCompact={isMobile || windowWidth <= 720}
            onPrefetch={(option) =>
              prefetchWellnessTile(option, {
                maxImages: 2,
                reason: 'nav-intent',
              })
            }
            onSelect={(selected) => {
              if (selected?.value === 'explore-wellness-app') {
                navigate(buildWellnessOnboardingPath())
              } else {
                navigate(buildWellnessTilePath(selected?.value), { state: null })
              }
            }}
          />
        </GlobalNavCenter>

        <GlobalNavRight>
          <SessionIcon
            onClick={(event) => {
              if (windowWidth > 520) {
                handleOpenBookedSlots(event)
              } else {
                setBookedSlotsDrawerOpen(true)
              }
            }}
          >
            1-on-1
            {eventsNotYetEndedList?.length > 0 && (
              <OneOnOneCountForIcon>{eventsNotYetEndedList?.length}</OneOnOneCountForIcon>
            )}
          </SessionIcon>

          {windowWidth > 520 && (
            <Popover
              id="booked-slots-popover"
              open={bookedSlotsOpen}
              anchorEl={anchorElBookedSlots}
              onClose={handleBookedSlotPopoverClosed}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <SlotNotificationPage
                partialBookedSlotsData={eventsNotYetEndedList}
                withMaxHeight
                profileData={profileData}
              />
            </Popover>
          )}

          <JournalIcon
            onClick={() =>
              setJournalData({
                showJournal: true,
                callSource: 'navbar',
                data: { callSourceId: 'navbar', sourceName: 'navbar' },
                pageState: '',
                subscription: '',
              })
            }
          />

          <NotificationsList />

          <PortalSwitcher darkText={false} isMobile={isMobile} />

          <GlobalNavProfileDropdown
            unSecureSetupData={unSecureSetupData}
            localizedStrings={localizedStrings}
            setBookedSlotsDrawerOpen={setBookedSlotsDrawerOpen}
            windowWidth={windowWidth}
          />
        </GlobalNavRight>
      </GlobalNavParent>

      {nextAvailableSlot && canEventBeJoined(nextAvailableSlot, currentTime) && (
        <NextSessionStrip
          nextAvailableSlot={nextAvailableSlot}
          currentTime={currentTime}
          eventsNotYetEndedList={eventsNotYetEndedList}
          showShowAllStrip={eventsNotYetEndedList?.length > 1}
          areAllStripsVisible={areAllStripsVisible}
          toggleShowAllStrips={() => setAreAllStripsVisible((prev) => !prev)}
        />
      )}

      {areAllStripsVisible &&
        eventsNotYetEndedList?.length > 1 &&
        eventsNotYetEndedList
          ?.slice(1)
          ?.map((item, index) => (
            <NextSessionStrip
              key={index}
              nextAvailableSlot={item}
              currentTime={currentTime}
              eventsNotYetEndedList={eventsNotYetEndedList}
              showShowAllStrip={false}
              areAllStripsVisible={areAllStripsVisible}
              toggleShowAllStrips={() => setAreAllStripsVisible((prev) => !prev)}
            />
          ))}

      {bookedSlotsDrawerOpen && windowWidth < 520 && (
        <BookedSlotsDrawer
          className="drawer"
          anchor="bottom"
          open={bookedSlotsDrawerOpen}
          transitionDuration={{ enter: 600, exit: 300 }}
        >
          <img
            alt="close_icon"
            src={close_icon_purple}
            style={{
              cursor: 'pointer',
              height: '20px',
              position: 'absolute',
              top: '30px',
              right: '30px',
            }}
            onClick={() => setBookedSlotsDrawerOpen(false)}
          />
          <SlotNotificationPage
            partialBookedSlotsData={eventsNotYetEndedList}
            profileData={profileData}
          />
        </BookedSlotsDrawer>
      )}

      <SEOComponent
        title={activePageLabel || 'AUMHUM'}
        description={'Explore AUMHUM content library.'}
        image={APP_CONSTANTS.APP_LOGO_LINK}
        keywordsArr={[]}
      />
    </GlobalNavContainer>
  )
}

export default GlobalNav
